import { useEffect, useRef, useState } from 'react';
import useMessageService from '../../services/i18n/useMessageService';
import VerifyCodeFormContext from './VerifyCodeFormContext';
import VerifyCodeFormDesktop from './VerifyCodeFormDesktop';
import VerifyCodeFormMobile from './VerifyCodeMobile';
import { isMobile } from '../../services/DeviceInformation';

const INPUTS = Object.freeze([
    'verification_input_one',
    'verification_input_two',
    'verification_input_three',
    'verification_input_four',
]);

const VerifyCodeForm = ({
    onChange,
    error,
    focusInputRef,
    onCodeResend,
    showResendClicked,
    onSubmit,
    resetRef,
    inputsDisabled,
    resendDisabled,
}) => {
    const [
        [
            inputLabel = 'Enter Your Verification Code',
            linkLabel = 'Resend code',
            codeSentLabel = 'New code sent',
        ],
    ] = useMessageService([
        'verify_code.enter_your_code',
        'verify_code.resend_code',
        'verify_code.code_sent',
    ]);

    // Example: ['X', 'Y', '', ''] means input 1 has 'X', input 2 has 'Y', Inputs 3 and 4 have no value.
    const [values, setValues] = useState(INPUTS.map(() => ''));
    const inputRefs = useRef([]);

    const setInputFocus = (index) => {
        inputRefs.current[index]?.focus();
    };

    useEffect(() => {
        const resetInputsCallback = () => {
            const newValues = INPUTS.map(() => '');
            setValues(newValues);
            onChange('');
        };
        resetRef(resetInputsCallback);
    }, [onChange, resetRef]);

    useEffect(() => {
        const focusInputCallback = (index = INPUTS.length - 1) => {
            setInputFocus(index);
        };
        focusInputRef(focusInputCallback);
    }, [focusInputRef]);

    const handleInputChange = (e, index) => {
        const enteredValue = e.target.value?.trim();
        if (enteredValue?.length === 1) {
            const newValues = [...values];
            newValues[index] = enteredValue;
            setValues(newValues);
            onChange(newValues.join(''));

            setInputFocus(index + 1);
        } else if (enteredValue?.length > 1) {
            // Example: Input has ['A', '', '', ''] and user pastes 'BC'
            // 1st piece: ['A'], 2nd piece: ['B', 'C'], 3rd piece: ['']
            const firstPiece = values.slice(0, index);
            const secondPiece = enteredValue.split('').slice(0, INPUTS.length - firstPiece.length);
            const thirdPiece = values.slice(firstPiece.length + secondPiece.length);
            const newValues = [...firstPiece, ...secondPiece, ...thirdPiece];
            setValues(newValues);
            onChange(newValues.join(''));

            const newFocusIndex = Math.min(INPUTS.length - 1, index + enteredValue.length);
            setInputFocus(newFocusIndex);
        } else if (enteredValue === '') {
            const newValues = [...values];
            newValues[index] = '';
            setValues(newValues);
            onChange(newValues.join(''));
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === 'Backspace' && !values[index]) {
            const newValues = [...values];
            newValues[index - 1] = '';
            setValues(newValues);
            onChange(newValues.join(''));

            setInputFocus(index - 1);
        } else if (e.key === 'Enter' && values.every((value) => !!value)) {
            onSubmit();
        }
    };

    return (
        <VerifyCodeFormContext.Provider
            value={{
                inputLabel,
                inputRefs,
                onInputChange: handleInputChange,
                onKeyDown: handleKeyDown,
                values,
                error,
                showResendClicked,
                codeSentLabel,
                onCodeResend,
                linkLabel,
                inputsDisabled,
                resendDisabled,
            }}
        >
            {isMobile() ? <VerifyCodeFormMobile /> : <VerifyCodeFormDesktop />}
        </VerifyCodeFormContext.Provider>
    );
};

export default VerifyCodeForm;
